import {verifiedFetchJSON} from '@github-ui/verified-fetch'

import type {
  CreateScribeArtifactData,
  CreateScribeArtifactInput,
  DeleteScribeArtifactData,
  DeleteScribeArtifactInput,
  DeleteScribeArtifactResult,
  GetScribeArtifactOptions,
  ListScribeArtifactsOptions,
  ReorderScribeArtifactInput,
  ScribeArtifact,
  ScribeArtifactDetailsPayload,
  ScribeArtifactResourceOptions,
  ScribeArtifactsListPayload,
  UpdateScribeArtifactData,
  UpdateScribeArtifactInput,
} from './types'

interface ScribeArtifactPayload {
  artifact: ScribeArtifact
}

const UNPROCESSABLE_ENTITY_STATUS = 422

export class ScribeArtifactsRequestError extends Error {
  status: number
  isValidationError: boolean

  constructor(message: string, status: number, {isValidationError = false}: {isValidationError?: boolean} = {}) {
    super(message)
    this.name = 'ScribeArtifactsRequestError'
    this.status = status
    this.isValidationError = isValidationError
  }
}

export function isScribeArtifactsValidationError(error: unknown): error is ScribeArtifactsRequestError {
  return error instanceof ScribeArtifactsRequestError && error.isValidationError
}

export function buildScribeArtifactsPath({
  resourceType,
  resourceId,
  type,
  page,
  perPage,
}: ListScribeArtifactsOptions): string {
  const params = new URLSearchParams({
    resource_type: resourceType,
    resource_id: String(resourceId),
  })
  if (type) params.set('type', type)
  if (page) params.set('page', String(page))
  if (perPage) params.set('per_page', String(perPage))

  return `/scribe/artifacts?${params.toString()}`
}

/** Builds the path to a single Scribe artifact, identified by its `number`. */
export function buildScribeArtifactPath(
  number: number,
  {resourceType, resourceId}: ScribeArtifactResourceOptions,
): string {
  const params = new URLSearchParams({
    resource_type: resourceType,
    resource_id: String(resourceId),
  })

  return `/scribe/artifacts/${number}?${params.toString()}`
}

/** Builds the controller path for moving an artifact relative to another artifact number. */
export function buildScribeArtifactReorderPath(number: number, options: ScribeArtifactResourceOptions): string {
  return buildScribeArtifactPath(number, options).replace(
    `/scribe/artifacts/${number}`,
    `/scribe/artifacts/${number}/reorder`,
  )
}

export async function listScribeArtifacts(options: ListScribeArtifactsOptions): Promise<ScribeArtifactsListPayload> {
  const response = await verifiedFetchJSON(buildScribeArtifactsPath(options), {signal: options.signal})
  await assertOk(response, 'Unable to list Scribe artifacts')

  const payload = (await response.json()) as ScribeArtifactsListPayload | ScribeArtifact[]
  return Array.isArray(payload)
    ? {artifacts: payload, has_next_page: false}
    : {artifacts: payload.artifacts, has_next_page: payload.has_next_page ?? false}
}

export async function getScribeArtifact({
  resourceType,
  resourceId,
  number,
  signal,
}: GetScribeArtifactOptions): Promise<ScribeArtifactDetailsPayload> {
  const response = await verifiedFetchJSON(buildScribeArtifactPath(number, {resourceType, resourceId}), {signal})
  await assertOk(response, 'Unable to get Scribe artifact')

  return (await response.json()) as ScribeArtifactDetailsPayload
}

export async function createScribeArtifact({
  resourceType,
  resourceId,
  ...input
}: CreateScribeArtifactInput): Promise<ScribeArtifact> {
  return createScribeArtifactAtPath(buildScribeArtifactsPath({resourceType, resourceId}), input)
}

export async function createScribeArtifactAtPath(
  path: string,
  {signal, name, description, body, type}: CreateScribeArtifactData & {signal?: AbortSignal},
): Promise<ScribeArtifact> {
  const response = await verifiedFetchJSON(path, {
    method: 'POST',
    signal,
    body: {
      name,
      description,
      body,
      type,
    },
  })
  await assertOk(response, 'Unable to create Scribe artifact')

  return parseScribeArtifact(response)
}

export async function updateScribeArtifact({
  resourceType,
  resourceId,
  number,
  signal,
  name,
  body,
}: UpdateScribeArtifactInput): Promise<ScribeArtifact> {
  return updateScribeArtifactAtPath(buildScribeArtifactPath(number, {resourceType, resourceId}), {
    signal,
    name,
    body,
  })
}

export async function updateScribeArtifactAtPath(
  path: string,
  {signal, name, body}: UpdateScribeArtifactData & {signal?: AbortSignal},
): Promise<ScribeArtifact> {
  const response = await verifiedFetchJSON(path, {
    method: 'PATCH',
    signal,
    body: {
      name,
      body,
    },
  })
  await assertOk(response, 'Unable to update Scribe artifact')

  return parseScribeArtifact(response)
}

export async function deleteScribeArtifact({
  resourceType,
  resourceId,
  number,
  signal,
}: DeleteScribeArtifactInput): Promise<DeleteScribeArtifactResult> {
  return deleteScribeArtifactAtPath(buildScribeArtifactPath(number, {resourceType, resourceId}), {number, signal})
}

export async function deleteScribeArtifactAtPath(
  path: string,
  {number, signal}: DeleteScribeArtifactData & {signal?: AbortSignal},
): Promise<DeleteScribeArtifactResult> {
  const response = await verifiedFetchJSON(path, {
    method: 'DELETE',
    signal,
  })
  await assertOk(response, 'Unable to delete Scribe artifact')

  let payload: Partial<DeleteScribeArtifactResult>
  try {
    payload = (await response.json()) as Partial<DeleteScribeArtifactResult>
  } catch {
    payload = {}
  }
  return {number: payload.number ?? number, deleted: payload.deleted ?? true}
}

export async function reorderScribeArtifact({
  resourceType,
  resourceId,
  number,
  afterNumber,
  signal,
}: ReorderScribeArtifactInput): Promise<ScribeArtifact> {
  const response = await verifiedFetchJSON(buildScribeArtifactReorderPath(number, {resourceType, resourceId}), {
    method: 'PATCH',
    signal,
    body: afterNumber === undefined ? {} : {after_number: afterNumber},
  })
  await assertOk(response, 'Unable to reorder Scribe artifact')

  return parseScribeArtifact(response)
}

async function parseScribeArtifact(response: Response): Promise<ScribeArtifact> {
  const payload = (await response.json()) as ScribeArtifactPayload | ScribeArtifact
  return 'artifact' in payload ? payload.artifact : payload
}

async function assertOk(response: Response, message: string): Promise<void> {
  if (response.ok) return

  const validationMessage = await validationErrorMessage(response)
  throw new ScribeArtifactsRequestError(validationMessage ?? message, response.status, {
    isValidationError: validationMessage !== undefined,
  })
}

async function validationErrorMessage(response: Response): Promise<string | undefined> {
  if (response.status !== UNPROCESSABLE_ENTITY_STATUS) return

  let payload: unknown
  try {
    payload = await response.json()
  } catch {
    return
  }

  if (typeof payload !== 'object' || payload === null || !('message' in payload)) return

  const {message} = payload
  return typeof message === 'string' && message.trim() ? message : undefined
}
